/**
 * FiPipelinePanel — read-only summary of the deals pipeline (DealsSummary).
 * Stage bars (raw vs weighted MRR) + top-line weighted totals. No CRUD —
 * deal management lives elsewhere; this is the owner cockpit's forecast view.
 */
import type { DealsSummary, DealStage } from './fiTypes'
import { DEAL_STAGES, STAGE_LABEL } from './fiTypes'
import { formatCurrency } from './fiFormat'

interface FiPipelinePanelProps {
  pipeline: DealsSummary
}

const STAGE_COLOR: Record<DealStage, string> = {
  prospect: '#64748b',
  poc: '#3b82f6',
  proposal: '#f59e0b',
  won: '#22c55e',
  lost: '#ef4444',
}

export default function FiPipelinePanel({ pipeline }: FiPipelinePanelProps) {
  const maxRawMrr = Math.max(...pipeline.byStage.map(s => s.rawMrr), 1)
  // Preserve canonical stage order even if the API returns a partial/unordered list.
  const stagesInOrder = DEAL_STAGES
    .map(stage => pipeline.byStage.find(s => s.stage === stage))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Pipeline de Deals</h3>
          <p className="text-gray-400 text-sm">Proyección — no es revenue reconocido</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-gray-900/50 border border-gray-700/60 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase tracking-wide">Deals Abiertos</p>
          <p className="text-xl font-bold text-white mt-0.5">{pipeline.openCount}</p>
        </div>
        <div className="bg-gray-900/50 border border-gray-700/60 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase tracking-wide">MRR Ponderado</p>
          <p className="text-xl font-bold text-white mt-0.5">{formatCurrency(pipeline.weightedPipelineMrr)}</p>
        </div>
        <div className="bg-gray-900/50 border border-gray-700/60 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase tracking-wide">One-time Ponderado</p>
          <p className="text-xl font-bold text-white mt-0.5">{formatCurrency(pipeline.weightedPipelineOneTime)}</p>
        </div>
        <div className="bg-gray-900/50 border border-gray-700/60 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase tracking-wide">Ganados / Perdidos (mes)</p>
          <p className="text-xl font-bold mt-0.5">
            <span className="text-green-400">{pipeline.wonThisMonth}</span>
            <span className="text-gray-600 mx-1">/</span>
            <span className="text-red-400">{pipeline.lostThisMonth}</span>
          </p>
        </div>
      </div>

      {stagesInOrder.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm">Sin deals registrados.</div>
      ) : (
        <div className="space-y-3">
          {stagesInOrder.map(stage => {
            const widthPct = (stage.rawMrr / maxRawMrr) * 100
            const weightedWidthPct = stage.rawMrr > 0 ? (stage.weightedMrr / stage.rawMrr) * widthPct : 0
            const color = STAGE_COLOR[stage.stage]
            return (
              <div key={stage.stage}>
                <div className="flex items-center justify-between mb-1 text-sm">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-white font-medium">{STAGE_LABEL[stage.stage]}</span>
                    <span className="text-gray-500 text-xs">({stage.count})</span>
                  </span>
                  <span className="text-gray-300">
                    {formatCurrency(stage.rawMrr)}
                    <span className="text-gray-500 text-xs ml-2">ponderado {formatCurrency(stage.weightedMrr)}</span>
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden relative">
                  <div className="h-full rounded-full absolute inset-y-0 left-0 opacity-30"
                    style={{ width: `${widthPct}%`, backgroundColor: color }} />
                  <div className="h-full rounded-full absolute inset-y-0 left-0"
                    style={{ width: `${weightedWidthPct}%`, backgroundColor: color }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
